import type { Entrant, Seed } from "./types.js";

/**
 * Put entrants in seeding order: strongest first.
 *
 * Two decisions worth stating, because both are visible to players and neither is forced:
 *
 * **The unrated seed last.** There is no number to sort them by, and inventing one — the
 * field average, the seed value from their self-assessment — would state a strength the app
 * does not know. Bottom of the draw is the conventional answer and the honest one: it costs
 * a newcomer a hard first-round opponent, which is exactly what an unknown quantity gets in
 * every tournament ever run. Note this is the opposite of how registration treats an absent
 * rating, where being unrated must not exclude you (§5.3). Seeding orders, it does not
 * refuse, so being last is a position rather than a door closed.
 *
 * **Ties break on member id, not on nothing.** Two identical ratings have to resolve
 * somehow, and leaving it to `Array.sort` stability makes the draw depend on the order rows
 * came back from Postgres. Regenerating a draw twice would then produce different brackets
 * from identical data, which is the kind of thing that gets noticed exactly once, in an
 * argument, at a tournament.
 *
 * **An organiser's seed outranks all of that.** Entrants with a `fixedSeed` come first, in that
 * order, and only the rest are ordered by rating. With no seeds anywhere this is the rating
 * order exactly, which is what lets an unseeded event draw as it always did.
 */
export function seedEntrants(entrants: Entrant[]): Seed[] {
  return [...entrants]
    .sort((a, b) => {
      const fixedA = a.fixedSeed ?? null;
      const fixedB = b.fixedSeed ?? null;
      if (fixedA != null && fixedB != null && fixedA !== fixedB) return fixedA - fixedB;
      if (fixedA != null && fixedB == null) return -1;
      if (fixedA == null && fixedB != null) return 1;

      if (a.rating == null && b.rating == null) return compareIds(a, b);
      if (a.rating == null) return 1;
      if (b.rating == null) return -1;
      if (a.rating !== b.rating) return b.rating - a.rating;
      return compareIds(a, b);
    })
    .map((entrant, index) => ({ ...entrant, seed: index + 1 }));
}

function compareIds(a: Entrant, b: Entrant): number {
  return a.memberIds.join(",").localeCompare(b.memberIds.join(","));
}
